/**
 * GoogleReviewsTab - Admin panel for managing Google Review display stats
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Save, RefreshCw, ExternalLink, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import { GoogleReviewCardFull, GoogleReviewCardCompact, GoogleReviewBadge } from '@/components/GoogleReviewCard';

export default function GoogleReviewsTab() {
  const [stats, setStats] = useState({
    rating: 4.9,
    total_reviews: 150,
    distribution_5: 85,
    distribution_4: 10,
    distribution_3: 3,
    distribution_2: 1,
    distribution_1: 1,
    google_review_url: '',
    google_maps_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch current stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/google-reviews/stats');
        const data = res.data;
        setStats({
          rating: data.rating,
          total_reviews: data.total_reviews,
          distribution_5: data.distribution['5'] || 85,
          distribution_4: data.distribution['4'] || 10,
          distribution_3: data.distribution['3'] || 3,
          distribution_2: data.distribution['2'] || 1,
          distribution_1: data.distribution['1'] || 1,
          google_review_url: data.google_review_url || '',
          google_maps_url: data.google_maps_url || ''
        });
        setLastUpdated(data.last_updated);
      } catch (err) {
        console.error('Failed to fetch review stats:', err);
      }
      setLoading(false);
    };
    fetchStats();
  }, []);

  // Save stats
  const handleSave = async () => {
    // Validate distribution adds up to ~100%
    const totalDist = stats.distribution_5 + stats.distribution_4 + stats.distribution_3 + 
                      stats.distribution_2 + stats.distribution_1;
    if (totalDist < 95 || totalDist > 105) {
      toast.error('Distribution percentages should add up to approximately 100%');
      return;
    }

    if (stats.rating < 1 || stats.rating > 5) {
      toast.error('Rating must be between 1 and 5');
      return;
    }

    setSaving(true);
    try {
      await api.put('/google-reviews/stats', stats);
      toast.success('Google Review stats updated!');
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      toast.error('Failed to update stats');
    }
    setSaving(false);
  };

  const totalDistribution = stats.distribution_5 + stats.distribution_4 + stats.distribution_3 + 
                            stats.distribution_2 + stats.distribution_1;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="google-reviews-tab">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl mb-2">Google Reviews Display</h2>
        <p className="text-muted-foreground">
          Update the Google Review stats shown on your website. These are display values - 
          update them to match your actual Google Business Profile reviews.
        </p>
        {lastUpdated && (
          <p className="text-xs text-muted-foreground mt-2">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </p>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900 dark:text-blue-100">How to get your actual stats</p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              1. Go to your <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Business Profile</a><br/>
              2. Look at your Reviews section for your current rating and review count<br/>
              3. Update the values below to match
            </p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Edit Form */}
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-heading text-lg mb-4">Review Statistics</h3>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div>
                <Label>Overall Rating (1-5)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="1"
                  max="5"
                  value={stats.rating}
                  onChange={(e) => setStats({ ...stats, rating: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="rating-input"
                />
              </div>
              <div>
                <Label>Total Reviews</Label>
                <Input
                  type="number"
                  min="0"
                  value={stats.total_reviews}
                  onChange={(e) => setStats({ ...stats, total_reviews: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="total-reviews-input"
                />
              </div>
            </div>

            <h4 className="font-medium mb-3">Rating Distribution (%)</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Total: {totalDistribution}% 
              {totalDistribution !== 100 && (
                <span className={totalDistribution > 95 && totalDistribution < 105 ? 'text-amber-500' : 'text-red-500'}>
                  {' '}(should be ~100%)
                </span>
              )}
            </p>
            
            <div className="space-y-3">
              {[5, 4, 3, 2, 1].map((stars) => (
                <div key={stars} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-12">
                    <span className="text-sm font-medium">{stars}</span>
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  </div>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={stats[`distribution_${stars}`]}
                    onChange={(e) => setStats({ 
                      ...stats, 
                      [`distribution_${stars}`]: parseInt(e.target.value) || 0 
                    })}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-400 rounded-full transition-all"
                      style={{ width: `${stats[`distribution_${stars}`]}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-heading text-lg mb-4">Links</h3>
            
            <div className="space-y-4">
              <div>
                <Label>Google Review URL</Label>
                <p className="text-xs text-muted-foreground mb-1">
                  The link where customers can leave a review
                </p>
                <Input
                  type="url"
                  value={stats.google_review_url}
                  onChange={(e) => setStats({ ...stats, google_review_url: e.target.value })}
                  placeholder="https://g.page/r/..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Google Maps URL</Label>
                <p className="text-xs text-muted-foreground mb-1">
                  Link to your Google Maps listing
                </p>
                <Input
                  type="url"
                  value={stats.google_maps_url}
                  onChange={(e) => setStats({ ...stats, google_maps_url: e.target.value })}
                  placeholder="https://www.google.com/maps/place/..."
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full rounded-full"
            data-testid="save-reviews-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        {/* Preview */}
        <div className="space-y-6">
          <div>
            <h3 className="font-heading text-lg mb-4">Live Preview</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This is how the review cards will appear on your site after saving.
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Full Card (Contact & About pages)</p>
              <GoogleReviewCardFull />
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Compact Card (Footer)</p>
              <GoogleReviewCardCompact />
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Badge (inline use)</p>
              <GoogleReviewBadge />
            </div>
          </div>

          <div className="bg-muted/50 rounded-xl p-4">
            <p className="text-sm text-muted-foreground">
              <strong>Tip:</strong> The preview above uses the current saved values from the database. 
              After you save changes, refresh the page to see the updated preview.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
